/**
 * Page registry — the client half of coarse page permissions.
 *
 * A Global Admin grants pages per system role; a page that is not granted is
 * hidden from the navigation and its route redirects. `page_key` is the
 * contract with the backend: these keys must match the `app_page` catalogue
 * seeded by V36__add_page_access.sql, and a pytest fails if the two drift.
 *
 * Child routes have no key of their own and are governed by their parent —
 * /documents/:id/review by 'documents', /tenant/questionnaires/:id/questions by
 * 'admin-questionnaires'. /questionnaire/:token is public and unauthenticated,
 * so it is deliberately absent.
 */

import { ROUTES } from './routes';

export const PAGE_KEYS = [
  'dashboard',
  'measurements',
  'programmes',
  'documents',
  'coaches',
  'coaches-history',
  'coach-members',
  'coach-requests',
  'coach-listing',
  'community',
  'agents',
  'manage-programmes',
  'manage-measurements',
  'admin-questionnaires',
  'tenant-settings',
  'manage-users',
  'manage-roles',
  'manage-role-permissions',
  'profile',
  'data-providers',
  'data-permissions',
  'settings',
  'admin-organisations',
  'admin-tenants',
  'admin-users',
  'admin-global-roles',
  'admin-global-permissions',
  'admin-roles',
  'admin-role-permissions',
  'admin-data-providers',
  'admin-document-types',
  'manage-data',
  'master-data-units',
] as const;

export type PageKey = (typeof PAGE_KEYS)[number];

/**
 * Pages that stay visible whatever the grants say, for someone whose app is an
 * organisation's.
 *
 * The server is authoritative — it adds these to every answer from
 * /pages/me — but the list is mirrored here for one purpose: if that request
 * fails we still render a usable app instead of an empty shell with no way to
 * reach a profile or sign out.
 */
export const CORE_PAGE_KEYS: readonly PageKey[] = ['dashboard', 'profile', 'settings'];

/**
 * The same fallback for a Global Admin, whose app is the administration area
 * and never contains the member pages above.
 *
 * Global permissions, because it is the screen that switches the others back
 * on — an admin who lands here after a failed /pages/me can still reach it.
 */
export const ADMIN_CORE_PAGE_KEYS: readonly PageKey[] = ['admin-global-permissions'];

/**
 * Where each page lives.
 *
 * One rule, established by V60 and checked by pageRegistry.test.ts: no page's
 * route may be a parent of another page's. A parent address stands for a set of
 * screens — a layout, a group's row, a redirect — and a page parked there has no
 * address of its own, so it cannot be linked to and its path matches every
 * sibling's. `ROUTES.root` is the exemption and the only one: it is the front
 * door, not a row, and nothing is a tab of it.
 *
 * These must also match `app_page.route_path`, which is what the sidebar links
 * to; `Api/tests/crud/test_app_page.py` reads this map to hold the two together.
 */
export const PAGE_ROUTES: Record<PageKey, string> = {
  dashboard: ROUTES.root,
  measurements: ROUTES.measurements,
  programmes: ROUTES.programmes,
  documents: ROUTES.documents,
  coaches: ROUTES.coachesMine,
  'coaches-history': ROUTES.coachesHistory,
  'coach-members': ROUTES.coachMembers,
  'coach-requests': ROUTES.coachRequests,
  'coach-listing': ROUTES.coachListing,
  community: ROUTES.community,
  agents: ROUTES.agent,
  'manage-programmes': ROUTES.tenantProgrammes,
  'manage-measurements': ROUTES.tenantProgrammeMeasurements,
  'admin-questionnaires': ROUTES.tenantQuestionnaires,
  'tenant-settings': ROUTES.tenantManage,
  'manage-users': ROUTES.tenantMembers,
  'manage-roles': ROUTES.tenantRoles,
  'manage-role-permissions': ROUTES.tenantRolePermissions,
  profile: ROUTES.profile,
  'data-providers': ROUTES.dataProviders,
  'data-permissions': ROUTES.dataPermissions,
  settings: ROUTES.settings,
  'admin-organisations': ROUTES.adminOrganisations,
  'admin-tenants': ROUTES.adminTenants,
  'admin-users': ROUTES.adminUsers,
  'admin-global-roles': ROUTES.adminGlobalRoles,
  'admin-global-permissions': ROUTES.adminGlobalPermissions,
  'admin-roles': ROUTES.adminRoles,
  'admin-role-permissions': ROUTES.adminRolePermissions,
  'admin-data-providers': ROUTES.adminDataProviders,
  'admin-document-types': ROUTES.adminDocumentTypes,
  'manage-data': ROUTES.masterDataMeasurements,
  'master-data-units': ROUTES.masterDataUnits,
};

const PAGE_KEY_SET: ReadonlySet<string> = new Set(PAGE_KEYS);

/** Narrow an arbitrary string from the API to a known page key. */
export const isPageKey = (value: string): value is PageKey => PAGE_KEY_SET.has(value);
